import api from '../../api'

// Phase 1 contract — keep co-located until a `lvbl_web/src/types/Court.ts` is established.
export type CourtType = 'pickle' | 'tennis' | 'padel'

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type CourtOperatingHours = {
  day_of_week: DayOfWeek
  open_time: string | null
  close_time: string | null
}

export type Court = {
  id: string
  venue: string
  name: string
  court_type: CourtType
  available_for_private_lessons: boolean
  operating_hours: CourtOperatingHours[]
  pending_approval?: boolean
}

export type CourtCreatePayload = {
  name: string
  court_type: CourtType
  available_for_private_lessons: boolean
  operating_hours: CourtOperatingHours[]
}

export type CourtPricingRow = {
  court_type: CourtType
  hourly_rate_cents: number
  currency: string
}

export type CourtPricing = {
  venue: string
  pricing: CourtPricingRow[]
}

export type CourtPricingPayload = {
  pricing: CourtPricingRow[]
}

export const COURT_TYPE_LABEL: Record<CourtType, string> = {
  pickle: 'Pickleball',
  tennis: 'Tennis',
  padel: 'Padel',
}

export const formatCourtOptionLabel = (court: Pick<Court, 'name' | 'court_type'>) =>
  COURT_TYPE_LABEL[court.court_type] ? `${court.name} — ${COURT_TYPE_LABEL[court.court_type]}` : court.name

const normalizeCourtsResponse = (payload: unknown): Court[] => {
  if (Array.isArray(payload)) {
    return payload as Court[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const maybeCourts = (payload as { courts?: unknown }).courts
  if (Array.isArray(maybeCourts)) {
    return maybeCourts as Court[]
  }

  const maybeData = (payload as { data?: unknown }).data
  if (Array.isArray(maybeData)) {
    return maybeData as Court[]
  }

  return []
}

export const getCourts = async ({ venueId }: { venueId: string }) => {
  const res = await api.get(`venues/${venueId}/courts/`)
  return normalizeCourtsResponse(res.data)
}

export const postCourt = async ({ venueId, payload }: { venueId: string; payload: CourtCreatePayload }) => {
  const res = await api.post(`venues/${venueId}/courts/`, payload)
  return res.data as Court
}

export const patchCourt = async ({
  venueId,
  courtId,
  payload,
}: {
  venueId: string
  courtId: string
  payload: Partial<CourtCreatePayload>
}) => {
  const res = await api.patch(`venues/${venueId}/courts/${courtId}/`, payload)
  return res.data as Court
}

export const deleteCourt = async ({
  venueId,
  courtId,
  reassignTo,
}: {
  venueId: string
  courtId: string
  reassignTo?: string
}) => {
  const url = reassignTo
    ? `venues/${venueId}/courts/${courtId}/?reassign_to=${reassignTo}`
    : `venues/${venueId}/courts/${courtId}/`
  const res = await api.delete(url)
  return res.data
}

export const getCourtPricing = async ({ venueId }: { venueId: string }) => {
  const res = await api.get(`venues/${venueId}/court_pricing/`)
  return res.data as CourtPricing
}

export const putCourtPricing = async ({ venueId, payload }: { venueId: string; payload: CourtPricingPayload }) => {
  const res = await api.put(`venues/${venueId}/court_pricing/`, payload)
  return res.data as CourtPricing
}
